import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getIn } from "formik";
import { AlertTriangle } from "lucide-react";

import {
  UICallout,
  UISelect,
  UITextarea,
  UITextField,
  UIToggle,
  UIUpload,
  UIRadioGroup,
} from "../../../../ui";
import Card from "../../../../Card";
import { Typography } from "@mui/material";
import { getLatLngFromAddress } from "../../../../../utils/Geocode";
import { clx } from "../../../../../utils/Helpers";
import { getEventTypeLookupOptionLabel } from "../../../../../utils/companyPortal/eventApi";

const getFieldError = (formik, name) => {
  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);
  return touched || formik.submitCount > 0 ? error : "";
};

const GEOCODE_DEBOUNCE_MS = 450;

const EventIdentitySection = ({
  formik,
  labels,
  eventTypeOptions = [],
  cityOptions = [],
  cityError,
  logoOptional,
  portalLogoSubmitLoading,
  portalLogoUploadPercent,
  canSelectPaymentPeriod,
  hasError,
  disableIdentityFields,
  disableRecurrenceFields,
  disableAddressField,
  flushGeocodeRef,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  /** Local data-URL preview while `image_url` holds a File (same idea as StepOne `img` state). */
  const [logoPreviewDataUrl, setLogoPreviewDataUrl] = useState(
    /** @type {string | null} */ (null),
  );

  /** @type {React.MutableRefObject<ReturnType<typeof setTimeout> | null>} */
  const geocodeTimerRef = useRef(null);
  const geocodeRequestIdRef = useRef(0);
  const [cityChangeWarning, setCityChangeWarning] = useState(false);

  useEffect(() => {
    if (typeof formik.values.image_url === "string") {
      setLogoPreviewDataUrl(null);
    }
  }, [formik.values.image_url]);

  /** Clears once the user has acted on the warning by touching the address. */
  useEffect(() => {
    setCityChangeWarning(false);
  }, [formik.values.address]);

  useEffect(() => {
    return () => {
      if (geocodeTimerRef.current) {
        clearTimeout(geocodeTimerRef.current);
      }
    };
  }, []);

  const clearLocationCoords = (shouldValidate) => {
    formik.setFieldValue(
      "location",
      {
        ...formik.values.location,
        lat: "",
        lng: "",
        address: "",
      },
      shouldValidate,
    );
  };

  const applyGeocodedCoords = (lat, lng, displayAddress) => {
    const addr = String(displayAddress ?? formik.values.address ?? "").trim();
    formik.setFieldValue(
      "location",
      {
        ...formik.values.location,
        lat,
        lng,
        address: addr,
      },
      true,
    );
  };

  /** Returns a promise that resolves once the resulting coords/failure are applied to formik. */
  const geocodeAddress = (raw) => {
    const q = String(raw ?? "").trim();
    if (!q) {
      clearLocationCoords(true);
      return Promise.resolve();
    }

    clearLocationCoords(false);

    const requestId = ++geocodeRequestIdRef.current;
    return new Promise((resolve) => {
      getLatLngFromAddress(
        q,
        (lat, lng) => {
          if (requestId === geocodeRequestIdRef.current) {
            applyGeocodedCoords(lat, lng, q);
          }
          resolve();
        },
        () => {
          if (requestId === geocodeRequestIdRef.current) {
            clearLocationCoords(true);
          }
          resolve();
        },
      );
    });
  };

  const scheduleGeocode = (value) => {
    if (geocodeTimerRef.current) {
      clearTimeout(geocodeTimerRef.current);
    }
    geocodeTimerRef.current = setTimeout(() => {
      geocodeTimerRef.current = null;
      geocodeAddress(value);
    }, GEOCODE_DEBOUNCE_MS);
  };

  /**
   * Resolves any still-pending debounced geocode immediately. Used before Save/Continue
   * validation so it can't run against a location that hasn't caught up with the typed
   * address yet (see PROLINE bug: form silently fails to save right after typing).
   */
  const flushGeocode = () => {
    if (!geocodeTimerRef.current) {
      return Promise.resolve();
    }
    clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = null;
    return geocodeAddress(formik.values.address);
  };

  if (flushGeocodeRef) {
    flushGeocodeRef.current = flushGeocode;
  }

  const imageUrlField = formik.values.image_url;
  const logoPreview =
    logoPreviewDataUrl ||
    (typeof imageUrlField === "string" ? imageUrlField : "");
  const logoFile = imageUrlField instanceof File ? imageUrlField : null;
  const logoUploadBusy = Boolean(portalLogoSubmitLoading && logoFile);
  const logoProgressLabel =
    logoUploadBusy && typeof portalLogoUploadPercent === "number"
      ? `${labels.logoUploading} · ${portalLogoUploadPercent}%`
      : logoUploadBusy
      ? labels.logoUploading
      : null;

  return (
    <Card
      title={labels.eventIdentity}
      classNames={{ root: clx({ "form-section--error": hasError }) }}
    >
      <div className="basic-info-identity__top">
        <UIUpload
          name="image_url"
          uploadText={labels.uploadLogo}
          variant="logo"
          preview={logoPreview}
          file={logoFile}
          showRemove
          required={!logoOptional}
          error={Boolean(!logoOptional && getFieldError(formik, "image_url"))}
          helperText={!logoOptional ? getFieldError(formik, "image_url") : ""}
          isUploading={logoUploadBusy}
          uploadProgress={
            typeof portalLogoUploadPercent === "number"
              ? portalLogoUploadPercent
              : undefined
          }
          uploadProgressLabel={logoProgressLabel}
          onChange={(file, preview) => {
            formik.setFieldTouched("image_url", true, false);
            if (!file) {
              formik.setFieldValue("image_url", "");
              setLogoPreviewDataUrl(null);
              return;
            }
            formik.setFieldValue("image_url", file);
            setLogoPreviewDataUrl(typeof preview === "string" ? preview : null);
          }}
          disabled={disableIdentityFields}
        />

        <div className="basic-info-identity__fields">
          <div className="basic-info-grid basic-info-grid--two">
            <UITextField
              name="name"
              label={labels.eventName}
              placeholder={labels.eventNamePlaceholder}
              required
              disabled={disableIdentityFields}
            />
            <UISelect
              name="event_type_id"
              label={labels.eventType}
              placeholder={labels.eventTypePlaceholder}
              options={eventTypeOptions}
              getOptionLabel={(option) =>
                getEventTypeLookupOptionLabel(option, lang)
              }
              value={
                eventTypeOptions.find(
                  (option) =>
                    formik.values.event_type_id != null &&
                    formik.values.event_type_id !== "" &&
                    String(option.id) ===
                      String(formik.values.event_type_id),
                ) || null
              }
              required
              disabled={disableIdentityFields}
              onChange={(nextValue) => {
                const id = nextValue?.id;
                formik.setFieldValue(
                  "event_type_id",
                  id !== "" && id != null ? id : null,
                );
              }}
              isOptionEqualToValue={(option, selected) =>
                Boolean(
                  option &&
                    selected &&
                    String(option.id) === String(selected.id),
                )
              }
            />
          </div>
          <div className="basic-info-grid basic-info-grid--two">
            <UISelect
              id="city_id"
              name="location_city_id"
              label={labels.city}
              placeholder={labels.cityPlaceholder}
              options={cityOptions}
              value={
                cityOptions.find(
                  (option) =>
                    formik.values.location?.city_id != null &&
                    formik.values.location?.city_id !== "" &&
                    String(option.id) === String(formik.values.location?.city_id),
                ) || null
              }
              required
              disabled={disableAddressField}
              error={Boolean(cityError)}
              helperText={cityError}
              onChange={(nextValue) => {
                const id = nextValue?.id;
                const previousId = formik.values.location?.city_id;
                const hasAddress = Boolean(
                  String(formik.values.address || "").trim(),
                );
                if (hasAddress && String(previousId) !== String(id)) {
                  setCityChangeWarning(true);
                }
                formik.setFieldValue("location", {
                  ...formik.values.location,
                  city_id: id !== "" && id != null ? id : null,
                });
              }}
              isOptionEqualToValue={(option, selected) =>
                Boolean(
                  option &&
                    selected &&
                    String(option.id) === String(selected.id),
                )
              }
            />
            <UITextField
              name="address"
              label={labels.locationName}
              placeholder={labels.locationNamePlaceholder}
              required
              disabled={disableAddressField}
              onValueChange={(value) => {
                if (!disableAddressField) {
                  scheduleGeocode(value);
                }
              }}
              onBlur={disableAddressField ? undefined : flushGeocode}
            />
          </div>
          {cityChangeWarning && (
            <UICallout
              variant="warning"
              className="basic-info-identity__city-warning"
              icon={
                <AlertTriangle
                  size={16}
                  strokeWidth={1.5}
                  color="var(--color-warning-default, #F59E0B)"
                />
              }
              description={labels.cityChangedVerifyAddress}
            />
          )}
        </div>
      </div>

      <UITextarea
        name="description"
        label={labels.eventBrief}
        placeholder={labels.eventBriefPlaceholder}
        required
        disabled={disableIdentityFields}
      />

      <div className="basic-info-divider" />

      <div className="basic-info-recurrence">
        <Typography variant="body1" className="event-form-container__label">
          {labels.recurrence} <span className="text-danger">*</span>
        </Typography>
        <div className="basic-info-recurrence__options">
          <UIRadioGroup
            name="payment_period"
            options={[
              { value: "daily", label: labels.daily },
              { value: "monthly", label: labels.monthly },
            ]}
            row
            required
            canSelectOption={canSelectPaymentPeriod}
            disabled={disableRecurrenceFields}
          />
          {formik.values.payment_period !== "monthly" && (
            <UIToggle
              name="is_single_day"
              checked={formik.values.is_single_day}
              label={labels.singleDayEvent}
              disabled={disableRecurrenceFields}
              onChange={(checked) =>
                formik.setFieldValue("is_single_day", checked)
              }
            />
          )}
        </div>
        {formik.values.payment_period === "monthly" && (
          <div className="basic-info-grid basic-info-grid--two">
            <UITextField
              name="number_of_days"
              label={labels.numberOfDays}
              placeholder={labels.numberOfDaysPlaceholder}
              type="number"
              required
              disabled={disableRecurrenceFields}
            />
            <UITextField
              name="paid_days_off"
              label={labels.paidDaysOff}
              placeholder={labels.paidDaysOffPlaceholder}
              type="number"
              required
              disabled={disableRecurrenceFields}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

EventIdentitySection.propTypes = {
  formik: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  eventTypeOptions: PropTypes.arrayOf(PropTypes.object),
  cityOptions: PropTypes.arrayOf(PropTypes.object),
  cityError: PropTypes.string,
  logoOptional: PropTypes.bool,
  portalLogoSubmitLoading: PropTypes.bool,
  portalLogoUploadPercent: PropTypes.number,
  canSelectPaymentPeriod: PropTypes.func,
  hasError: PropTypes.bool,
  disableIdentityFields: PropTypes.bool,
  disableRecurrenceFields: PropTypes.bool,
  disableAddressField: PropTypes.bool,
  flushGeocodeRef: PropTypes.shape({ current: PropTypes.any }),
};

EventIdentitySection.defaultProps = {
  eventTypeOptions: [],
  cityOptions: [],
  cityError: "",
  logoOptional: true,
  portalLogoSubmitLoading: false,
  portalLogoUploadPercent: undefined,
  canSelectPaymentPeriod: undefined,
  hasError: false,
  disableIdentityFields: false,
  disableRecurrenceFields: false,
  disableAddressField: false,
  flushGeocodeRef: undefined,
};

export default EventIdentitySection;
